import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, useMotionValue, useTransform, AnimatePresence } from "framer-motion";
import { Heart, X, Users, User, Sparkles, Copy, Check, ArrowRight, RotateCcw, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, fileUrl } from "@/lib/api";
import { formatPrezzo } from "@/data/properties";
import useSEO from "@/hooks/useSEO";

function SwipeCard({ property, onDecide, active }) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-220, 220], [-16, 16]);
  const likeOpacity = useTransform(x, [40, 140], [0, 1]);
  const nopeOpacity = useTransform(x, [-140, -40], [1, 0]);
  const img = fileUrl((property.immagini || [])[0]);

  return (
    <motion.div
      className="absolute inset-0 rounded-[2rem] overflow-hidden bg-white border border-border-subtle shadow-[0_30px_80px_-30px_rgba(15,92,74,0.45)] cursor-grab active:cursor-grabbing"
      style={{ x, rotate, touchAction: "pan-y" }}
      drag={active ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.7}
      onDragEnd={(e, info) => {
        if (info.offset.x > 120) onDecide(true);
        else if (info.offset.x < -120) onDecide(false);
      }}
      whileTap={{ scale: 0.99 }}
      data-testid={`crush-card-${property.id}`}
    >
      <div className="relative h-full">
        <img src={img} alt={property.titolo} className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable={false} />
        <div className="absolute inset-0 bg-gradient-to-t from-anthracite/85 via-anthracite/10 to-transparent" />

        <motion.span style={{ opacity: likeOpacity }} className="absolute top-8 left-8 rotate-[-12deg] border-4 border-brand text-brand font-display text-4xl font-bold rounded-2xl px-4 py-1 bg-white/90">
          MI PIACE
        </motion.span>
        <motion.span style={{ opacity: nopeOpacity }} className="absolute top-8 right-8 rotate-[12deg] border-4 border-red-500 text-red-500 font-display text-4xl font-bold rounded-2xl px-4 py-1 bg-white/90">
          NO
        </motion.span>

        <div className="absolute bottom-0 left-0 right-0 p-7 text-white pointer-events-none">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 backdrop-blur px-3 py-1 text-xs font-semibold capitalize">
            {property.transazione} • {property.tipo}
          </span>
          <h3 className="font-display text-3xl sm:text-4xl font-semibold mt-3 leading-tight">{property.titolo}</h3>
          <p className="mt-1.5 flex items-center gap-1.5 text-white/80 text-sm"><MapPin className="h-4 w-4" /> {property.comune} — {property.zona}</p>
          <p className="mt-2 font-display text-2xl font-semibold">{formatPrezzo(property)}</p>
        </div>
      </div>
    </motion.div>
  );
}

function MatchLeadModal({ open, onOpenChange, likedTitles, mode }) {
  const [form, setForm] = useState({ nome: "", telefono: "", email: "" });
  const [consenso, setConsenso] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.telefono) { toast.error("Inserisci nome e telefono."); return; }
    if (!consenso) { toast.error("Accetta l'informativa privacy."); return; }
    try {
      await api.post("/public/leads", {
        tipo: "casa_crush",
        contatto: { nome: form.nome, telefono: form.telefono, email: form.email || null },
        payload: { Modalità: mode === "coppia" ? "In coppia" : "Singolo", "Immobili preferiti": likedTitles.join(" · ") || "—" },
        consensoPrivacy: true,
        provenienza: "Casa Crush",
      });
      toast.success("Perfetto! Ti contatteremo per organizzare le visite.");
      onOpenChange(false);
      setForm({ nome: "", telefono: "", email: "" }); setConsenso(false);
    } catch (err) {
      if (err?.response?.status === 429) toast.error("Troppe richieste, riprova tra poco.");
      else toast.error("Invio non riuscito, riprova.");
    }
  };
  const fld = "bg-white border-border-subtle focus-visible:ring-brand/30 focus-visible:border-brand rounded-xl h-12";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-warm border-0 rounded-3xl" data-testid="crush-lead-modal">
        <DialogHeader><DialogTitle className="font-display text-3xl font-semibold text-anthracite">Organizziamo le visite</DialogTitle></DialogHeader>
        <DialogDescription className="text-sm text-anthracite/60 -mt-2">Lasciaci i tuoi contatti: ti aiuteremo a visitare gli immobili che ti sono piaciuti.</DialogDescription>
        <form onSubmit={submit} className="space-y-3 mt-2" data-testid="crush-lead-form">
          <Input className={fld} placeholder="Nome e cognome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="crush-nome" />
          <Input className={fld} placeholder="Telefono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="crush-telefono" />
          <Input className={fld} type="email" placeholder="Email (facoltativa)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="crush-email" />
          <label className="flex items-start gap-2 text-xs text-anthracite/60">
            <input type="checkbox" checked={consenso} onChange={(e) => setConsenso(e.target.checked)} className="mt-0.5 accent-[#0F5C4A]" data-testid="crush-privacy" />
            Acconsento al trattamento dei dati secondo l'informativa privacy.
          </label>
          <button type="submit" className="w-full rounded-full bg-brand text-white font-semibold py-4 hover:bg-brand-hover transition-colors" data-testid="crush-submit">Invia richiesta</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CasaCrush() {
  useSEO({ title: "Casa Crush — Trova la casa che ti fa battere il cuore | Casamore Group", description: "Scorri gli immobili di Casamore Group, metti mi piace a quelli che ami e scopri le tue case preferite. Disponibile anche la modalità in coppia.", canonical: window.location.origin + "/casa-crush" });
  const [params, setParams] = useSearchParams();
  const joinCode = params.get("crush");

  const [mode, setMode] = useState(joinCode ? "coppia" : null);
  const [started, setStarted] = useState(!!joinCode);
  const [side] = useState(joinCode ? "B" : "A");
  const [sessionCode, setSessionCode] = useState(joinCode || null);
  const [copied, setCopied] = useState(false);

  const [properties, setProperties] = useState([]);
  const [index, setIndex] = useState(0);
  const [likes, setLikes] = useState([]);
  const [serverMatches, setServerMatches] = useState([]);
  const [leadOpen, setLeadOpen] = useState(false);

  useEffect(() => {
    api.get("/public/casa-crush/properties").then((r) => setProperties(r.data)).catch(() => setProperties([]));
  }, []);

  const shareLink = sessionCode ? `${window.location.origin}/casa-crush?crush=${sessionCode}` : "";

  const startSolo = () => { setMode("solo"); setStarted(true); };
  const startCouple = async () => {
    try {
      const r = await api.post("/public/casa-crush/session");
      setSessionCode(r.data.code);
      setMode("coppia");
      setParams({ crush: r.data.code });
    } catch { toast.error("Impossibile creare la sessione, riprova."); }
  };

  const fetchMatches = useCallback(() => {
    if (!sessionCode) return;
    api.get(`/public/casa-crush/session/${sessionCode}`).then((r) => setServerMatches(r.data.matches)).catch(() => {});
  }, [sessionCode]);

  useEffect(() => {
    if (mode === "coppia" && started) {
      fetchMatches();
      const t = setInterval(fetchMatches, 4000);
      return () => clearInterval(t);
    }
  }, [mode, started, fetchMatches]);

  const decide = (liked) => {
    const p = properties[index];
    if (!p) return;
    if (liked) setLikes((l) => [...l, p]);
    if (mode === "coppia" && sessionCode) {
      api.post(`/public/casa-crush/session/${sessionCode}/swipe`, { side, propertyId: p.id, liked })
        .then((r) => setServerMatches(r.data.matches)).catch(() => {});
    }
    setIndex((i) => i + 1);
  };

  const copyLink = () => { navigator.clipboard.writeText(shareLink); setCopied(true); setTimeout(() => setCopied(false), 1800); };
  const reset = () => { setIndex(0); setLikes([]); };

  const done = started && index >= properties.length && properties.length > 0;
  const likedTitles = useMemo(() => likes.map((p) => p.titolo), [likes]);

  // ---------- Intro ----------
  if (!started) {
    return (
      <div className="pt-32 pb-24 min-h-[80vh]">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-brand">
              <Sparkles className="h-4 w-4" /> Casa Crush
            </span>
            <h1 className="font-display font-semibold text-anthracite text-5xl sm:text-6xl tracking-tight mt-4 leading-[0.95]">
              Trova la casa che <span className="italic text-brand">ti fa battere il cuore</span>
            </h1>
            <p className="mt-5 text-lg text-anthracite/70">
              Scorri gli immobili: trascina a destra quelli che ami, a sinistra quelli che scarti.
              Alla fine ti mostriamo le tue preferite e organizziamo le visite.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button onClick={startSolo} className="group rounded-3xl border border-border-subtle bg-white p-7 text-left transition-all hover:border-brand hover:shadow-lg" data-testid="crush-mode-solo">
                <span className="grid place-items-center h-12 w-12 rounded-2xl bg-brand/10 text-brand"><User className="h-6 w-6" /></span>
                <p className="font-display text-2xl font-semibold text-anthracite mt-4">Da solo</p>
                <p className="text-sm text-anthracite/60 mt-1">Scopri le case che preferisci in autonomia.</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">Inizia <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
              </button>
              <button onClick={startCouple} className="group rounded-3xl border border-border-subtle bg-white p-7 text-left transition-all hover:border-brand hover:shadow-lg" data-testid="crush-mode-couple">
                <span className="grid place-items-center h-12 w-12 rounded-2xl bg-brand/10 text-brand"><Users className="h-6 w-6" /></span>
                <p className="font-display text-2xl font-semibold text-anthracite mt-4">In coppia</p>
                <p className="text-sm text-anthracite/60 mt-1">Invita il partner: vedrete le case che piacciono a entrambi.</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">Crea sessione <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
              </button>
            </div>

            {mode === "coppia" && sessionCode && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-2xl bg-accent-subtle p-5 text-left" data-testid="crush-share-box">
                <p className="text-sm font-semibold text-anthracite">Condividi questo link con il partner</p>
                <div className="mt-2 flex items-center gap-2">
                  <input readOnly value={shareLink} className="flex-1 h-11 rounded-xl border border-border-subtle bg-white px-3 text-sm" data-testid="crush-share-link" />
                  <button onClick={copyLink} className="inline-flex items-center gap-1.5 rounded-xl bg-brand text-white px-4 h-11 text-sm font-semibold" data-testid="crush-copy">
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copiato" : "Copia"}
                  </button>
                </div>
                <button onClick={() => setStarted(true)} className="mt-4 w-full rounded-full bg-anthracite text-white font-semibold py-3.5" data-testid="crush-start-couple">Inizia a scorrere</button>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    );
  }

  // ---------- Deck / Results ----------
  return (
    <div className="pt-28 pb-24 min-h-[90vh]">
      <div className="mx-auto max-w-xl px-6">
        <div className="flex items-center justify-between">
          <Link to="/casa-crush" onClick={() => { setStarted(false); setMode(null); }} className="text-sm text-anthracite/50 hover:text-brand">← Casa Crush</Link>
          {mode === "coppia" && <span className="text-xs font-semibold text-brand bg-accent-subtle rounded-full px-3 py-1.5" data-testid="crush-couple-badge">In coppia • {serverMatches.length} match</span>}
        </div>

        {!done ? (
          <>
            <div className="relative aspect-[3/4] mt-6" data-testid="crush-deck">
              <AnimatePresence>
                {properties.slice(index, index + 2).reverse().map((p, i, arr) => {
                  const isTop = i === arr.length - 1;
                  return (
                    <div key={p.id} className="absolute inset-0" style={{ transform: isTop ? "none" : "scale(0.95) translateY(14px)", zIndex: isTop ? 2 : 1 }}>
                      <SwipeCard property={p} active={isTop} onDecide={decide} />
                    </div>
                  );
                })}
              </AnimatePresence>
            </div>

            <div className="mt-7 flex items-center justify-center gap-6">
              <button onClick={() => decide(false)} className="grid place-items-center h-16 w-16 rounded-full bg-white border border-border-subtle text-red-500 shadow-md transition-transform hover:scale-110" data-testid="crush-nope-btn">
                <X className="h-7 w-7" strokeWidth={2.5} />
              </button>
              <button onClick={() => decide(true)} className="grid place-items-center h-16 w-16 rounded-full bg-brand text-white shadow-lg transition-transform hover:scale-110" data-testid="crush-like-btn">
                <Heart className="h-7 w-7" />
              </button>
            </div>
            <p className="text-center text-sm text-anthracite/40 mt-4">{index}/{properties.length} • trascina o usa i pulsanti</p>
          </>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 text-center" data-testid="crush-results">
            <span className="grid place-items-center h-16 w-16 rounded-full bg-brand text-white mx-auto"><Heart className="h-8 w-8" /></span>
            <h2 className="font-display text-4xl font-semibold text-anthracite mt-5">
              {mode === "coppia" ? "Le vostre case in comune" : "Le tue case preferite"}
            </h2>
            {(mode === "coppia" ? serverMatches : likes).length === 0 ? (
              <p className="mt-3 text-anthracite/60">{mode === "coppia" ? "Ancora nessun match in comune. Aspetta che anche il partner finisca!" : "Non hai messo mi piace a nessun immobile."}</p>
            ) : (
              <div className="mt-6 space-y-3 text-left">
                {(mode === "coppia" ? serverMatches : likes).map((p) => (
                  <Link key={p.id} to={`/immobili/${p.id}`} className="flex items-center gap-4 rounded-2xl bg-white border border-border-subtle p-3 hover:border-brand transition-colors" data-testid={`crush-match-${p.id}`}>
                    <img src={fileUrl((p.immagini || [])[0])} alt="" className="h-16 w-20 rounded-xl object-cover" />
                    <div className="flex-1">
                      <p className="font-semibold text-anthracite">{p.titolo}</p>
                      <p className="text-sm text-anthracite/60">{p.comune} — {p.zona} • {formatPrezzo(p)}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-brand" />
                  </Link>
                ))}
              </div>
            )}
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => setLeadOpen(true)} className="rounded-full bg-brand text-white font-semibold px-7 py-4 hover:bg-brand-hover transition-colors" data-testid="crush-organize-btn">Organizza le visite</button>
              <button onClick={reset} className="inline-flex items-center justify-center gap-2 rounded-full border border-border-subtle text-anthracite/70 font-semibold px-7 py-4 hover:border-brand hover:text-brand transition-colors" data-testid="crush-restart-btn">
                <RotateCcw className="h-4 w-4" /> Ricomincia
              </button>
            </div>
          </motion.div>
        )}
      </div>

      <MatchLeadModal open={leadOpen} onOpenChange={setLeadOpen} likedTitles={likedTitles} mode={mode} />
    </div>
  );
}
